<template>
  <section class="dp-layout">
    <section class="dp-right log-page">
    <header class="log-page__head">
      <div>
        <h3 class="log-page__title">班牌 MQTT 流水</h3>
        <p class="log-page__desc">连接生命周期与指令上下行；单次最长 {{ maxRangeDays }} 天，不按部门筛选</p>
      </div>
    </header>

    <div class="toolbar-filter-bar">
      <el-form :inline="true" class="toolbar-filter-form" @submit.native.prevent>
        <div class="toolbar-filter-fields">
        <el-form-item label="时间">
          <el-date-picker
            v-model="filters.dateRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            start-placeholder="开始"
            end-placeholder="结束"
            :default-time="['00:00:00', '23:59:59']"
            class="toolbar-field-w-xl"
          />
        </el-form-item>
        <el-form-item label="设备SN">
          <el-input v-model="filters.serialNumber" clearable placeholder="SN" class="toolbar-field-w-xl" />
        </el-form-item>
        <el-form-item label="ClientId">
          <el-input v-model="filters.clientId" clearable placeholder="ClientId" class="toolbar-field-w-xl" />
        </el-form-item>
        <el-form-item label="事件">
          <el-select v-model="filters.eventKind" clearable placeholder="全部" class="toolbar-field-w-sm">
            <el-option label="连接校验" value="Validating" />
            <el-option label="已连接" value="Connected" />
            <el-option label="已断开" value="Disconnected" />
            <el-option label="认证失败" value="AuthFail" />
            <el-option label="指令下发" value="CmdDown" />
            <el-option label="指令上报" value="CmdUp" />
          </el-select>
        </el-form-item>
        <el-form-item label="指令">
          <el-select
            v-model="filters.cmd"
            clearable
            filterable
            placeholder="全部"
            class="toolbar-field-w-md"
          >
            <el-option
              v-for="item in cmdOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </el-form-item>
        </div>
      </el-form>
    </div>

    <div class="tbl-scroll-host">
      <el-table
        :data="users"
        v-loading="listLoading"
        ref="table"
        class="log-table custom-tbl"
        height="100%"
        stripe
        highlight-current-row
        style="width: 100%"
      >
        <el-table-column prop="CreateTime" label="时间" width="158" />
        <el-table-column prop="EventKind" label="事件" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain" :type="eventTagType(scope.row.EventKind, scope.row.Cmd)">
              {{ eventLabel(scope.row.EventKind, scope.row.Cmd) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="DisconnectType" label="断开类型" width="100">
          <template slot-scope="scope">
            <span v-if="scope.row.EventKind === 'Disconnected'" class="disconnect-pill">
              {{ disconnectTypeLabel(scope.row.DisconnectType) }}
            </span>
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="SerialNumber" label="SN" width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            <code class="mono-cell">{{ scope.row.SerialNumber || '—' }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="ClientId" label="ClientId" width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            <code class="mono-cell">{{ scope.row.ClientId || '—' }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="Cmd" label="指令" width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ cmdLabel(scope.row.Cmd) }}
          </template>
        </el-table-column>
        <el-table-column prop="Type" label="方向" width="70" align="center">
          <template slot-scope="scope">
            <span v-if="isServerAckRow(scope.row)" class="dir-pill dir-pill--ack">应答</span>
            <span v-else-if="scope.row.Type === 0" class="dir-pill dir-pill--down">下发</span>
            <span v-else-if="scope.row.Type === 1" class="dir-pill dir-pill--up">上行</span>
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="回复" width="88" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.ReplyStatus === 1"
              size="mini"
              type="success"
              effect="plain"
            >已回复</el-tag>
            <el-tag
              v-else-if="scope.row.ReplyStatus === 0"
              size="mini"
              type="info"
              effect="plain"
            >未回复</el-tag>
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="ResponseCode" width="88" align="center">
          <template slot="header">
            <span>Code</span>
            <el-tooltip placement="top" effect="dark">
              <div slot="content" class="code-tip">
                应答码 ResponseCode，按事件含义不同：<br />
                -1：无业务/协议应答码（连接、下发等常见）<br />
                0：断开时多为 MQTT 正常断开 ReasonCode<br />
                401：鉴权失败<br />
                其它：设备上行指令应答里的 code
              </div>
              <i class="el-icon-info col-header-tip" />
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="ResponseMessage" label="消息" min-width="140" show-overflow-tooltip />
        <el-table-column prop="Endpoint" label="地址" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            <code class="mono-cell">{{ scope.row.Endpoint || '—' }}</code>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" class="detail-link" @click="showDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-col :span="24" class="toolbar">
      <el-pagination
        layout="total, prev, pager, next, jumper"
        @current-change="handleCurrentChange"
        :page-size="pageSize"
        :total="total"
        style="float: right"
      />
    </el-col>

    <el-dialog
      :visible.sync="detailVisible"
      v-loading="detailLoading"
      width="780px"
      custom-class="log-detail-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <div slot="title" class="log-detail-dialog__title">
        <span>MQTT 流水详情</span>
        <el-tag
          v-if="current"
          size="mini"
          effect="plain"
          :type="eventTagType(current.EventKind, current.Cmd)"
        >{{ eventLabel(current.EventKind, current.Cmd) }}</el-tag>
      </div>

      <template v-if="current">
        <div class="meta-grid">
          <div class="meta-item">
            <div class="meta-item__label">时间</div>
            <div class="meta-item__value">{{ current.CreateTime || '—' }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">事件</div>
            <div class="meta-item__value">{{ eventLabel(current.EventKind, current.Cmd) }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">断开类型</div>
            <div class="meta-item__value">
              <template v-if="current.EventKind === 'Disconnected'">
                {{ disconnectTypeLabel(current.DisconnectType) }}
              </template>
              <template v-else>—</template>
            </div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">指令</div>
            <div class="meta-item__value">{{ cmdLabel(current.Cmd) }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">方向</div>
            <div class="meta-item__value">
              <span v-if="isServerAckRow(current)">应答</span>
              <span v-else-if="current.Type === 0">下发</span>
              <span v-else-if="current.Type === 1">上行</span>
              <span v-else>—</span>
            </div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">回复状态</div>
            <div class="meta-item__value">
              <template v-if="current.ReplyStatus === 1">
                <el-tag size="mini" type="success" effect="plain">已回复</el-tag>
              </template>
              <template v-else-if="current.ReplyStatus === 0">
                <el-tag size="mini" type="info" effect="plain">未回复</el-tag>
              </template>
              <template v-else>—</template>
            </div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">Code</div>
            <div class="meta-item__value">{{ current.ResponseCode }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">SN</div>
            <div class="meta-item__value mono-cell">{{ current.SerialNumber || '—' }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">ClientId</div>
            <div class="meta-item__value mono-cell">{{ current.ClientId || '—' }}</div>
          </div>
          <div class="meta-item">
            <div class="meta-item__label">地址</div>
            <div class="meta-item__value mono-cell">{{ current.Endpoint || '—' }}</div>
          </div>
          <div class="meta-item meta-item--wide">
            <div class="meta-item__label">消息</div>
            <div class="meta-item__value">{{ current.ResponseMessage || '—' }}</div>
          </div>
          <div class="meta-item meta-item--wide">
            <div class="meta-item__label">RequestId</div>
            <div class="meta-item__value mono-cell">{{ current.RequestId || '—' }}</div>
          </div>
        </div>

        <div
          v-if="current.ReplyStatus === 1"
          class="payload-block reply-summary"
        >
          <div class="payload-block__head">
            <span>{{ isDeviceInitiatedCmd(current.Cmd) ? '关联应答' : '关联回复' }}</span>
          </div>
          <div class="reply-summary__body">
            <div>时间：{{ current.ReplyTime || '—' }}</div>
            <div>应答码：{{ current.ReplyCode == null ? '—' : current.ReplyCode }}</div>
            <div>消息：{{ current.ReplyMessage || '—' }}</div>
          </div>
        </div>

        <div class="payload-block">
          <div class="payload-block__head">
            <span>RequestData</span>
            <el-button type="text" class="payload-copy" icon="el-icon-document-copy" @click="copyText(current.RequestData || '', 'RequestData')">复制</el-button>
          </div>
          <pre class="payload-block__body">{{ current.RequestData || '（空）' }}</pre>
        </div>
        <div class="payload-block">
          <div class="payload-block__head">
            <span>ResponseData</span>
            <el-button type="text" class="payload-copy" icon="el-icon-document-copy" @click="copyText(current.ResponseData || '', 'ResponseData')">复制</el-button>
          </div>
          <pre class="payload-block__body">{{ current.ResponseData || '（空）' }}</pre>
        </div>
      </template>

      <div slot="footer" class="dialog-footer">
        <el-button type="primary" plain icon="el-icon-document-copy" @click="copyDetailAll">复制全部</el-button>
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
    </section>
  </section>
</template>

<script>
import { getMqttDeviceLogPage, getMqttDeviceLogDetail } from '../../api/modules/mqttDeviceLog';

const MAX_RANGE_DAYS = 7;

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  // 结束默认当天 24 点（23:59:59）。最后更新：2026-07-24
  end.setHours(23, 59, 59, 0);
  const fmt = (d) => {
    const p = (n) => (n < 10 ? '0' + n : '' + n);
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  };
  return [fmt(start), fmt(end)];
}

export default {
  name: 'MqttDeviceLog',
  data() {
    return {
      maxRangeDays: MAX_RANGE_DAYS,
      filters: {
        dateRange: defaultRange(),
        serialNumber: '',
        clientId: '',
        eventKind: '',
        cmd: ''
      },
      cmdOptions: [
        { value: 'heartbeat', label: '心跳' },
        { value: 'getConfig', label: '获取配置' },
        { value: 'updateConfig', label: '更新配置' },
        { value: 'uploadAuthResult', label: '上传授权结果' },
        { value: 'logReporting', label: '日志上报' },
        { value: 'shutdown', label: '关机' },
        { value: 'reboot', label: '重启' },
        { value: 'ota', label: 'OTA升级' },
        { value: 'resourceNotification', label: '资源更新通知' },
        { value: 'userChangeNotification', label: '人员变动通知' },
        { value: 'userAddNotification', label: '人员增量通知' },
        { value: 'setPowerOnOffWithWeekly', label: '周定时开关机' },
        { value: 'setPowerOnOff', label: '定时开关机' },
        { value: 'clearPowerOnOffTime', label: '清除定时开关机' },
        { value: 'takeScreenshot', label: '截屏' },
        { value: 'rotateScreen', label: '屏幕旋转' },
        { value: 'changeScreenLight', label: '设置亮度' },
        { value: 'turnOffBacklight', label: '关背光' },
        { value: 'turnOnBacklight', label: '开背光' }
      ],
      users: [],
      total: 0,
      page: 1,
      pageSize: 10,
      listLoading: false,
      detailVisible: false,
      detailLoading: false,
      current: null
    };
  },
  mounted() {
    this.handleQuery();
  },
  methods: {
    eventTagType(kind, cmd) {
      if (kind === 'CmdDown' && this.isDeviceInitiatedCmd(cmd)) return 'info';
      if (kind === 'Connected' || kind === 'CmdUp') return 'success';
      if (kind === 'AuthFail' || kind === 'Disconnected') return 'danger';
      if (kind === 'CmdDown') return 'warning';
      return 'info';
    },
    /**
     * 终端发起类指令（上行后由服务端应答）。与后端 MqttCmdFlowHelper 保持一致。最后更新：2026-07-24
     */
    isDeviceInitiatedCmd(cmd) {
      if (!cmd) return false;
      const c = String(cmd).toLowerCase();
      return c === 'heartbeat' || c === 'getconfig' || c === 'uploadauthresult';
    },
    /**
     * 服务端对上行的应答行（非业务下发）。最后更新：2026-07-24
     */
    isServerAckRow(row) {
      if (!row || row.EventKind !== 'CmdDown') return false;
      if (this.isDeviceInitiatedCmd(row.Cmd)) return true;
      // 业务下发默认 ResponseCode=-1；应答形态会写入真实 code
      return row.ResponseCode != null && Number(row.ResponseCode) !== -1;
    },
    eventLabel(kind, cmd) {
      if (kind === 'CmdDown' && this.isDeviceInitiatedCmd(cmd)) return '服务端应答';
      const map = {
        Validating: '连接校验',
        Connected: '已连接',
        Disconnected: '已断开',
        AuthFail: '认证失败',
        CmdDown: '指令下发',
        CmdUp: '指令上报'
      };
      return map[kind] || kind || '—';
    },
    directionLabel(row) {
      if (!row) return '—';
      if (this.isServerAckRow(row)) return '应答';
      if (row.Type === 0) return '下发';
      if (row.Type === 1) return '上行';
      return '—';
    },
    disconnectTypeLabel(t) {
      const map = {
        Clean: '正常断开',
        NotClean: '异常断开',
        Takeover: '会话被顶替'
      };
      return map[t] || t || '—';
    },
    cmdLabel(cmd) {
      if (!cmd) return '—';
      const hit = this.cmdOptions.find((x) => x.value === cmd);
      if (hit) return hit.label;
      return this.eventLabel(cmd);
    },
    /**
     * 复制文本到剪贴板。最后更新：2026-07-22
     */
    copyText(text, label) {
      const value = text == null ? '' : String(text);
      if (!value || value === '—' || value === '（空）') {
        this.$message.warning((label || '内容') + '为空，无需复制');
        return;
      }
      const done = () => this.$message.success((label || '内容') + '已复制');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => this.fallbackCopy(value, done));
        return;
      }
      this.fallbackCopy(value, done);
    },
    fallbackCopy(text, done) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', 'readonly');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        if (typeof done === 'function') done();
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本');
      }
      document.body.removeChild(ta);
    },
    /**
     * 复制当前 MQTT 流水详情。最后更新：2026-07-22
     */
    copyDetailAll() {
      if (!this.current) return;
      const c = this.current;
      const dir = this.directionLabel(c);
      const disconnect =
        c.EventKind === 'Disconnected' ? this.disconnectTypeLabel(c.DisconnectType) : '—';
      const lines = [
        `时间: ${c.CreateTime || ''}`,
        `事件: ${this.eventLabel(c.EventKind, c.Cmd)}`,
        `断开类型: ${disconnect}`,
        `指令: ${this.cmdLabel(c.Cmd)}`,
        `方向: ${dir}`,
        `Code: ${c.ResponseCode}`,
        `SN: ${c.SerialNumber || ''}`,
        `ClientId: ${c.ClientId || ''}`,
        `地址: ${c.Endpoint || ''}`,
        `消息: ${c.ResponseMessage || ''}`,
        `RequestId: ${c.RequestId || ''}`,
        '',
        '--- RequestData ---',
        c.RequestData || '（空）',
        '',
        '--- ResponseData ---',
        c.ResponseData || '（空）'
      ];
      this.copyText(lines.join('\n'), 'MQTT流水详情');
    },
    handleCurrentChange(val) {
      this.page = val;
      this.handleQuery();
    },
    handleSearch() {
      this.page = 1;
      this.handleQuery();
    },
    ensureRangeOk(dateRange) {
      if (!dateRange || dateRange.length !== 2) {
        this.$message.warning('请选择查询时间');
        return false;
      }
      const start = new Date(String(dateRange[0]).replace(/-/g, '/')).getTime();
      const end = new Date(String(dateRange[1]).replace(/-/g, '/')).getTime();
      if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
        this.$message.warning('时间范围无效');
        return false;
      }
      if ((end - start) / (24 * 3600 * 1000) > MAX_RANGE_DAYS) {
        this.$message.warning(`查询时间跨度不能超过 ${MAX_RANGE_DAYS} 天`);
        return false;
      }
      return true;
    },
    handleQuery() {
      const dateRange = this.filters.dateRange;
      if (!this.ensureRangeOk(dateRange)) return;

      const para = {
        page: this.page,
        pageSize: this.pageSize,
        startTime: dateRange[0],
        endTime: dateRange[1],
        serialNumber: this.filters.serialNumber || undefined,
        clientId: this.filters.clientId || undefined,
        eventKind: this.filters.eventKind || undefined,
        cmd: this.filters.cmd || undefined,
        knownTotal: this.page > 1 ? this.total : undefined
      };

      this.listLoading = true;
      getMqttDeviceLogPage(para)
        .then((res) => {
          const pageData = (res.data && res.data.response) || {};
          this.total = pageData.dataCount || 0;
          this.users = pageData.data || [];
        })
        .catch(() => {
          this.$message.error('查询失败');
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    showDetail(row) {
      this.current = { ...row };
      this.detailVisible = true;
      this.detailLoading = true;
      getMqttDeviceLogDetail(row.Id, row.CreateTime)
        .then((res) => {
          const detail = (res.data && res.data.response) || null;
          if (detail) {
            this.current = {
              ...detail,
              ReplyStatus: row.ReplyStatus,
              ReplyTime: row.ReplyTime,
              ReplyCode: row.ReplyCode,
              ReplyMessage: row.ReplyMessage
            };
          }
        })
        .catch(() => {
          this.$message.error('MQTT 流水详情加载失败');
        })
        .finally(() => {
          this.detailLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.log-page {
  --log-ink: #0f172a;
  --log-muted: #64748b;
  --log-line: #e2e8f0;
  --log-panel: #ffffff;
  --log-soft: #f8fafc;
  color: var(--log-ink);
}

.log-page__head {
  margin-bottom: 8px;
}

.log-page__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.log-page__desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--log-muted);
  line-height: 1.5;
}

.log-table >>> th {
  background: var(--log-soft) !important;
  color: #334155 !important;
  font-weight: 600;
}

.col-header-tip {
  margin-left: 2px;
  color: #94a3b8;
  cursor: help;
  font-size: 13px;
  vertical-align: middle;
}

.col-header-tip:hover {
  color: #64748b;
}

.log-table >>> .el-table__row:hover > td {
  background: #f1f5f9 !important;
}

.mono-cell {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  color: #334155;
}

.muted {
  color: #94a3b8;
}

.disconnect-pill {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 12px;
}

.dir-pill {
  display: inline-block;
  min-width: 40px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
}

.dir-pill--down {
  background: #fff7ed;
  color: #ea580c;
}

.dir-pill--ack {
  background: #eff6ff;
  color: #2563eb;
}

.dir-pill--up {
  background: #f0fdf4;
  color: #16a34a;
}

.detail-link {
  cursor: pointer;
  font-weight: 500;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 16px;
  margin-bottom: 14px;
  padding: 12px 14px;
  background: #fafbfc;
  border: 1px solid #eef2f6;
  border-radius: 8px;
}

.meta-item {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 2px 0;
}

.meta-item--wide {
  grid-column: span 2;
}

.meta-item__label {
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 2px;
  letter-spacing: 0.02em;
}

.meta-item__value {
  font-size: 13px;
  color: #334155;
  word-break: break-all;
  line-height: 1.45;
}

.payload-block {
  border: 1px solid #e8edf2;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
  background: #fff;
}

.payload-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px 6px 12px;
  background: #f7f9fb;
  border-bottom: 1px solid #eef2f6;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.payload-copy {
  padding: 0 4px !important;
  font-size: 12px !important;
  color: #64748b !important;
  cursor: pointer;
}

.payload-copy:hover {
  color: var(--btn-primary, #2563eb) !important;
}

.payload-block__body {
  margin: 0;
  padding: 10px 12px;
  max-height: 200px;
  overflow: auto;
  background: #f8fafc;
  color: #475569;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.reply-summary__body {
  padding: 10px 12px;
  font-size: 13px;
  color: #334155;
  line-height: 1.7;
  background: #f8fafc;
}

.log-detail-dialog__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #334155;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 900px) {
  .meta-grid {
    grid-template-columns: 1fr 1fr;
  }

  .meta-item--wide {
    grid-column: span 2;
  }
}
</style>

<style>
.log-detail-dialog .el-dialog__body {
  padding-top: 12px;
}
</style>
